import React from 'react';
import { useTranslation } from 'react-i18next';

import { QBox, QStack, QText } from '@captivateiq/quotient';

import { useAIShellContext } from '@captivateiq/assistant/AIShellContext';

import browserWelcomeIllustrationUrl from '@captivateiq/assistant/assets/png/browser-welcome-illustration.png';
import { useAgentDiscovery } from '@captivateiq/assistant/hooks/useAgentDiscovery';
import { AgentCard } from './AgentCard';
import { TabLayout } from '../../TabLayout';
import { expandedTextColumnSx } from '../../shared/expandedTextColumnSx';

export const BrowserView = () => {
  const { t } = useTranslation('assistant');
  const { availableAgents, loading, error } = useAgentDiscovery();
  const { isExpanded } = useAIShellContext();

  return (
    <TabLayout>
      <QStack
        direction="column"
        sx={{
          minHeight: '100%',
          ...expandedTextColumnSx(isExpanded),
        }}
      >
        <QBox sx={{ flexGrow: 1 }} />
        <QStack
          spacing="20px"
          sx={{
            alignItems: 'center',
            textAlign: 'center',
            padding: '24px 16px 20px',
          }}
        >
          <QStack
            sx={{
              position: 'relative',
              alignItems: 'center',
              justifyContent: 'center',
              '&::before': {
                content: '""',
                position: 'absolute',
                top: '49%',
                left: '37%',
                transform: 'translate(-50%, -50%)',
                width: '112px',
                height: '112px',
                borderRadius: '50%',
                background: 'radial-gradient(circle, rgba(124, 58, 237, 0.32) 0%, rgba(124, 58, 237, 0) 70%)',
                filter: 'blur(12px)',
                zIndex: 0,
              },
            }}
          >
            <img
              alt=""
              height={92}
              src={browserWelcomeIllustrationUrl}
              style={{ width: 'auto', position: 'relative', zIndex: 1 }}
            />
          </QStack>
          <QStack spacing="8px" sx={{ alignItems: 'center' }}>
            <QText
              sx={{
                fontSize: '17px',
                fontWeight: 700,
                color: 'text.onSurface.background.highContrast',
              }}
            >
              {t('browser_welcome_title')}
            </QText>
            <QText
              sx={{
                fontSize: '13px',
                fontWeight: 400,
                lineHeight: '20px',
                color: 'text.onSurface.background.mediumContrast',
                maxWidth: '280px',
              }}
            >
              {t('browser_welcome')}
            </QText>
          </QStack>
        </QStack>

        {loading && (
          <QStack sx={{ padding: '12px' }}>
            <QText sx={{ color: 'text.onSurface.background.mediumContrast' }}>
              {t('loading_agents', 'Loading available agents...')}
            </QText>
          </QStack>
        )}

        {error && (
          <QStack sx={{ padding: '12px' }}>
            <QText sx={{ color: 'text.onSurface.background.destructive' }}>
              {t('error_loading_agents', 'Error loading agents: {{error}}', { error })}
            </QText>
          </QStack>
        )}

        {!loading && !error && availableAgents.length === 0 && (
          <QStack sx={{ padding: '12px' }}>
            <QText sx={{ color: 'text.onSurface.background.mediumContrast' }}>
              {t('no_agents_available', 'No agents are currently available for this page.')}
            </QText>
          </QStack>
        )}

        {!loading && !error && availableAgents.length > 0 && (
          <QStack spacing="10px">
            {availableAgents.map((agent) => (
              <AgentCard agent={agent} key={agent.name} />
            ))}
          </QStack>
        )}
        <QBox sx={{ flexGrow: 2 }} />
      </QStack>
    </TabLayout>
  );
};
